/**
 * Solution Run Store — Zustand store for the "Invoke Solution Run" overlay form.
 *
 * WHY a separate store (DR-3):
 * - esaDomainStore manages conversation/thread lifecycle — a different concern.
 * - Form fields need clean reset on cancel without touching ESA state.
 * - Independent subscription lets InvokeSolutionOverlay and SolutionRunCard both
 *   subscribe without coupling to each other.
 *
 * WHY window singleton:
 * - Follows canvasAwarenessStore precedent (DR-7 of feat-09).
 * - MF-safe: if multiple React trees exist, they share the same store instance.
 */

import { create, type StoreApi, type UseBoundStore } from 'zustand';
import { devtools } from 'zustand/middleware';
import type {
  SolutionOption,
  Guardrail,
  InvokeSolutionPayload,
  SolutionRunSnapshot,
  PresetSelection,
} from '../types/solutionRun';
import { SOLUTION_RUN_STORE_WINDOW_KEY } from '../../../constants/storageKeys';

// ── State interface ────────────────────────────────────────────────────────────

export interface SolutionRunState {
  /**
   * Lifecycle status:
   *  idle       — no active run
   *  drafting   — modal open, user is filling the form
   *  reviewing  — modal closed, ESA is reviewing the run (awaiting agent response)
   *  initiating — "Initiate Run" clicked, request in flight
   *  error      — initiating failed
   *  degraded   — bootstrap failed, form still usable
   */
  status: 'idle' | 'drafting' | 'reviewing' | 'initiating' | 'error' | 'degraded';
  /** Linked ESA conversation ID. Set after bootstrapConversation() succeeds. */
  conversationId: string | null;
  /** Original payload stored so "Keep Editing" can reopen the overlay without re-bootstrap. */
  originalPayload: InvokeSolutionPayload | null;
  /** Currently selected solution. */
  solution: SolutionOption | null;
  /** Free-text data scope description (optional, empty string = not set). */
  dataScope: string;
  /** Free-text objective statement (optional, empty string = not set). */
  objective: string;
  /** All guardrails for this run (presets + user-added). */
  guardrails: Guardrail[];
  /** Solutions available for selection (from InvokeSolutionPayload). */
  availableSolutions: SolutionOption[];
  /** IDs of preset guardrails — these cannot be removed by the user (DR-7). */
  presetGuardrailIds: string[];
  /**
   * Explorer-grid rows the user had selected when the invocation was triggered.
   * When non-null the data-scope field is locked to this selection in the overlay.
   */
  presetSelection: PresetSelection | null;
}

// ── Actions interface ──────────────────────────────────────────────────────────

export interface SolutionRunActions {
  /** Initialize from an INVOKE_SOLUTION_RUN event payload. Sets status to 'drafting'. */
  initialize(payload: InvokeSolutionPayload): void;
  /** Update the selected solution. */
  setSolution(solution: SolutionOption): void;
  /** Update the data scope text. */
  setDataScope(value: string): void;
  /** Update the objective text. */
  setObjective(value: string): void;
  /** Add a user-authored guardrail. Assigns a unique id automatically. */
  addGuardrail(text: string): void;
  /**
   * Remove a guardrail by id.
   * Silently no-ops if the id is in presetGuardrailIds (preset lock, DR-7).
   */
  removeGuardrail(id: string): void;
  /** Record the real ESA conversation id returned by bootstrapConversation(). */
  setConversationId(id: string): void;
  /** Transition to 'reviewing' — modal has closed, ESA is processing the review. */
  setReviewing(): void;
  /** Transition back to 'drafting' — "Keep Editing" reopens the modal with current data intact. */
  setDrafting(): void;
  /** Transition to 'initiating' — "Initiate Run" clicked, request in flight. */
  setInitiating(): void;
  /** Transition to 'error' — sendMessage() failed after "Review & Initiate". */
  setError(): void;
  /**
   * Transition to 'degraded' — createConversation() failed during bootstrap
   * but the form is still usable (AR2-002). The card shows "ESA sync unavailable".
   */
  setDegraded(): void;
  /** Build a snapshot of the current form state for sending to the ESA agent. */
  getSnapshot(): SolutionRunSnapshot;
  /** Reset all state to 'idle'. Called on SOLUTION_RUN_INITIATED or SOLUTION_RUN_CANCELLED. */
  reset(): void;
}

export type SolutionRunStore = SolutionRunState & SolutionRunActions;

// ── Default state ──────────────────────────────────────────────────────────────

const DEFAULT_STATE: SolutionRunState = {
  status: 'idle',
  conversationId: null,
  originalPayload: null,
  solution: null,
  dataScope: '',
  objective: '',
  guardrails: [],
  availableSolutions: [],
  presetGuardrailIds: [],
  presetSelection: null,
};

// ── Factory ────────────────────────────────────────────────────────────────────

export function createSolutionRunStore(): UseBoundStore<StoreApi<SolutionRunStore>> {
  return create<SolutionRunStore>()(
    devtools(
      (set, get) => ({
        ...DEFAULT_STATE,

        initialize(payload) {
          const selected =
            payload.solutions.find(s => s.id === payload.defaultSolutionId) ??
            payload.solutions[0] ??
            null;

          const sel = payload.presetSelection ?? null;
          // Seed dataScope with a human-readable string so the ESA card and
          // the agent context automatically reflect the explorer selection.
          const seededDataScope = sel
            ? `${sel.count} record${sel.count === 1 ? '' : 's'} you selected`
            : '';

          set(
            {
              status: 'drafting',
              solution: selected,
              availableSolutions: payload.solutions,
              guardrails: [...payload.presetGuardrails],
              presetGuardrailIds: payload.presetGuardrails.map(g => g.id),
              dataScope: seededDataScope,
              objective: '',
              conversationId: null,
              originalPayload: payload,
              presetSelection: sel,
            },
            false,
            'solutionRun/initialize'
          );
        },

        setSolution(solution) {
          set({ solution }, false, 'solutionRun/setSolution');
        },

        setDataScope(value) {
          set({ dataScope: value }, false, 'solutionRun/setDataScope');
        },

        setObjective(value) {
          set({ objective: value }, false, 'solutionRun/setObjective');
        },

        addGuardrail(text) {
          const newGuardrail: Guardrail = {
            id: `user-${Date.now()}-${Math.random().toString(36).slice(2)}`,
            text,
            source: 'user',
          };
          set(
            state => ({ guardrails: [...state.guardrails, newGuardrail] }),
            false,
            'solutionRun/addGuardrail'
          );
        },

        removeGuardrail(id) {
          const { presetGuardrailIds } = get();
          // Preset guardrails are locked — silently no-op
          if (presetGuardrailIds.includes(id)) return;
          set(
            state => ({ guardrails: state.guardrails.filter(g => g.id !== id) }),
            false,
            'solutionRun/removeGuardrail'
          );
        },

        setConversationId(id) {
          set({ conversationId: id }, false, 'solutionRun/setConversationId');
        },

        setReviewing() {
          set({ status: 'reviewing' }, false, 'solutionRun/setReviewing');
        },

        setDrafting() {
          set({ status: 'drafting' }, false, 'solutionRun/setDrafting');
        },

        setInitiating() {
          set({ status: 'initiating' }, false, 'solutionRun/setInitiating');
        },

        setError() {
          set({ status: 'error' }, false, 'solutionRun/setError');
        },

        setDegraded() {
          set({ status: 'degraded' }, false, 'solutionRun/setDegraded');
        },

        getSnapshot() {
          const { solution, dataScope, objective, guardrails, presetSelection } = get();
          return {
            // solution is guaranteed non-null when status !== 'idle'
            solution: solution!,
            dataScope: dataScope.trim() || null,
            objective: objective.trim() || null,
            guardrails: [...guardrails],
            // Carried silently — not rendered in the card, consumed by the ESA agent
            selectedRecordIds: presetSelection?.recordIds ?? null,
          };
        },

        reset() {
          set({ ...DEFAULT_STATE }, false, 'solutionRun/reset');
        },
      }),
      { name: 'SolutionRun' }
    )
  );
}

// ── Window singleton (MF-safe) ─────────────────────────────────────────────────

const STORE_KEY = SOLUTION_RUN_STORE_WINDOW_KEY;

type SolutionRunStoreInstance = UseBoundStore<StoreApi<SolutionRunStore>>;

declare global {
  interface Window {
    [STORE_KEY]?: SolutionRunStoreInstance;
  }
}

/**
 * Get or create the solution-run store singleton.
 * Safe to call from any MF bundle — always returns the same Zustand store instance.
 */
export function getSolutionRunStore(): SolutionRunStoreInstance {
  if (typeof window !== 'undefined') {
    if (!window[STORE_KEY]) {
      window[STORE_KEY] = createSolutionRunStore();
    }
    return window[STORE_KEY]!;
  }
  return createSolutionRunStore();
}
